import { WaterGlassProduct } from './water-glass.js';
import './cartridge-navigation.js?v=20260909-prefetch';

/** Pack choices resolve to real variant IDs and quantities; plan prices come from allocations. */
export class CartridgeProduct extends WaterGlassProduct {
  connectedCallback() {
    if (this.controller) return;
    super.connectedCallback();
    this.syncPack();
    this.updateTotals();
  }

  onClick(event) {
    if (this.hasAttribute('data-navigating')) return;
    super.onClick(event);
    const pack = event.target.closest('[data-pack]');
    if (!pack || pack.disabled || this.busy) return;
    const variant = this.data.variants.find(v => String(v.id) === pack.dataset.variant);
    if (!variant || !this.validQuantity(variant, Number(pack.dataset.packQuantity))) return;
    const previousPlan = this.planSelect?.value;
    this.activePack = pack;
    this.variant = variant;
    this.variantSelect.value = String(variant.id);
    this.quantity.value = pack.dataset.packQuantity;
    this.updateVariant(true);
    if (previousPlan && this.planSelect && variant.plans.length) {
      this.planSelect.value = String(variant.plans.find(p => String(p.id) === previousPlan)?.id || variant.plans[0].id);
      this.updateTotals();
    }
  }

  onChange(event) {
    if (this.hasAttribute('data-navigating')) return;
    if (event.target.matches('[data-plan-frequency]')) {
      const plan = this.variant?.plans.find(p => String(p.id) === event.target.value);
      if (!plan || !this.planSelect || this.busy) return;
      this.preferredPlanId = plan.id;
      // Previewing a cadence is not consent to a subscription: the Refill radio opts in.
      if (this.planSelect.value || this.data.requiresPlan) this.planSelect.value = String(plan.id);
      this.updateTotals();
      return;
    }
    if (event.target.matches('[data-purchase-mode]')) {
      if (!this.planSelect || !this.variant) return;
      if (event.target.value === 'plan') this.planSelect.value = String(this.variant.plans.find(p => p.id === this.preferredPlanId)?.id || this.variant.plans[0]?.id || '');
      else if (!this.data.requiresPlan) this.planSelect.value = '';
      this.updateTotals();
      return;
    }
    super.onChange(event);
    if (event.target === this.variantSelect || event.target === this.quantity) {
      this.syncPack();
      this.updateTotals();
    }
  }

  validQuantity(variant, quantity) {
    return Number.isInteger(quantity) && quantity >= variant.min && (quantity - variant.min) % variant.step === 0 && (variant.max == null || quantity <= variant.max);
  }

  updateVariant(updateURL) {
    const state = history.state;
    if (this.variant && this.quantity && !this.validQuantity(this.variant, Number(this.quantity.value))) this.quantity.value = this.variant.min;
    super.updateVariant(updateURL);
    if (updateURL) history.replaceState(state, '', location.href);
    // Keep hash-only Back/Forward distinct from changing products or pack variants.
    if (updateURL && window.durynCartridgeNavigation) window.durynCartridgeNavigation.committedURL = location.href;
  }

  syncPack() {
    this.activePack = [...this.querySelectorAll('[data-pack]')].find(p => p.dataset.variant === String(this.variant?.id) && Number(p.dataset.packQuantity) === Number(this.quantity?.value));
  }

  updateTotals() {
    super.updateTotals();
    if (!this.variant) return;
    const packs = [...this.querySelectorAll('[data-pack]')];
    this.toggleAttribute('data-show-native', this.data.variants.some(v => !packs.some(p => p.dataset.variant === String(v.id))));
    const quantity = Number(this.quantity?.value || this.variant.min);
    const selectedPlan = this.variant.plans.find(p => String(p.id) === this.planSelect?.value);
    if (selectedPlan) this.preferredPlanId = selectedPlan.id;
    const offeredPlan = selectedPlan || this.variant.plans.find(p => p.id === this.preferredPlanId) || this.variant.plans[0];
    const active = this.activePack && this.activePack.dataset.variant === String(this.variant.id) && Number(this.activePack.dataset.packQuantity) === quantity ? this.activePack : packs.find(p => p.dataset.variant === String(this.variant.id) && Number(p.dataset.packQuantity) === quantity);
    this.activePack = active;
    for (const pack of packs) {
      const v = this.data.variants.find(x => String(x.id) === pack.dataset.variant);
      const qty = Number(pack.dataset.packQuantity), count = Number(pack.dataset.count);
      pack.disabled = this.busy || !v?.available || !this.validQuantity(v, qty);
      pack.setAttribute('aria-pressed', String(pack === active));
      const p = selectedPlan ? v?.plans.find(x => String(x.id) === String(selectedPlan.id)) || v?.plans[0] : null;
      // A pack change uses that pack's actual allocations and displays its real plan name.
      if (selectedPlan && !p) pack.disabled = true;
      pack.querySelector('[data-pack-price]').textContent = v ? this.format((p?.price ?? v.price) * qty / count) + ' ' + this.dataset.packEach : '';
      const discount = pack.querySelector('[data-pack-saving]');
      const baseline = v?.compare > v?.price ? v.compare : v?.price;
      const amount = p?.price ?? v?.price;
      discount.hidden = !(baseline > amount);
      discount.textContent = baseline > amount ? this.dataset.save + ' ' + Math.round((1 - amount / baseline) * 100) + '%' : '';
    }
    const write = (selector, value) => { const node = this.querySelector(selector); if (node) node.textContent = value; };
    write('[data-once-total]', this.format(this.variant.price * quantity));
    write('[data-plan-total]', offeredPlan ? this.format(offeredPlan.price * quantity) : '');
    write('[data-plan-name]', offeredPlan?.name || '');
    const saving = offeredPlan && this.variant.price > offeredPlan.price ? Math.round((1 - offeredPlan.price / this.variant.price) * 100) : 0;
    write('[data-plan-saving]', saving ? this.dataset.save + ' ' + saving + '%. ' : '');
    this.querySelector('[data-refill-card]')?.toggleAttribute('data-disabled', !offeredPlan);
    this.querySelectorAll('[data-purchase-mode]').forEach(radio => {
      radio.disabled = this.busy || (radio.value === 'plan' ? !offeredPlan : !!this.data.requiresPlan);
      radio.checked = radio.value === (selectedPlan ? 'plan' : 'once');
    });
    const frequency = this.querySelector('[data-plan-frequency]');
    if (frequency) {
      const optionsKey = JSON.stringify(this.variant.plans.map(p => [p.id, p.name]));
      if (frequency.dataset.optionsKey !== optionsKey) {
        frequency.replaceChildren(...this.variant.plans.map(p => new Option(p.name, String(p.id))));
        frequency.dataset.optionsKey = optionsKey;
      }
      frequency.value = String(offeredPlan?.id || '');
      frequency.disabled = this.busy || !offeredPlan;
      this.querySelector('[data-frequency-field]').hidden = !offeredPlan;
    }
    if (this.planSelect) this.planSelect.hidden = frequency ? true : this.querySelector('[data-purchase-mode]') ? !selectedPlan || this.variant.plans.length < 2 : false;
    const price = selectedPlan?.price ?? this.variant.price;
    const compare = selectedPlan ? this.variant.price : this.variant.compare;
    const compareNode = this.querySelector('[data-compare]');
    if (compareNode) {
      compareNode.hidden = !(compare > price);
      compareNode.textContent = compare > price ? this.format(compare * quantity) : '';
    }
    const count = active ? Number(active.dataset.count) : 0;
    const months = Number(this.dataset.months);
    write('[data-unit-summary]', count ? (months > 0 ? '≈ ' + this.format(price * quantity / (count * months)) + ' ' + this.dataset.perMonth + ' · ' : '') + this.format(price * quantity / count) + ' ' + this.dataset.each : '');
    this.querySelectorAll('[data-submit]').forEach(button => {
      button.dataset.label = selectedPlan ? this.dataset.planLabel : this.dataset.onceLabel;
      if (!this.busy && this.variant.available && (!this.data.requiresPlan || selectedPlan)) button.querySelector('[data-submit-label]').textContent = button.dataset.label;
      if (!this.validQuantity(this.variant, quantity)) button.disabled = true;
    });
  }

  async submit(event) {
    if (this.hasAttribute('data-navigating')) { event.preventDefault(); return; }
    if (!this.variant || !this.validQuantity(this.variant, Number(this.quantity?.value))) { event.preventDefault(); return; }
    if (this.planSelect?.value && !this.variant.plans.some(p => String(p.id) === this.planSelect.value)) { event.preventDefault(); return; }
    return super.submit(event);
  }
}

if (!customElements.get('cartridge-product')) customElements.define('cartridge-product', CartridgeProduct);
